// src/utils/floorPlanOrigin.js
//
// Tesisat pozisyonlarını kat planına göre relative (local) olarak saklamak için
// kullanılan yardımcılar.
//
// Sorun: Kat planı grubunun (THREE.Group) world pozisyonu layout her çalıştığında
// değişebiliyor (yuklemeCetveli/kolonSema yükseklikleri veriye göre değiştiği
// için currentX/yOffset farklı çıkıyor). Tesisat pozisyonları mutlak world
// koordinat olarak saklanırsa save → reload arasında kat planı kayar ama
// tesisat kayar gibi görünür (mesh kayar değil, kat planı altından kayar).
//
// Çözüm: Tesisat oluşturma anında kat planının world origin'ini al, position'ı
// local (relative) olarak depola. Rebuild anında yine current origin'i alıp
// world'e çevir. Layout farklı pozisyon üretse bile tesisat kat planına relative
// olduğu için doğru hizalanır.

import * as THREE from 'three';
import { getGlobalDocumentBounds } from '../../utils/getActiveFloorPlanId';

// Scene'i tutmak için global ref — DrawingCanvas onSceneReady'de set ediyor.
// floorPlanGroup'un gerçek world pozisyonunu almak için traverse'a ihtiyacımız
// var; documentBounds.minX/maxY documentGroup'un position'u ama floor plan
// içerik o noktadan bbox-normalize + layoutOffset ile kaymış olabiliyor.
let _sceneRef = null;
export function setGlobalSceneRef(scene) { _sceneRef = scene; }
export function getGlobalSceneRef() { return _sceneRef; }

/**
 * Scene'de floorPlanId'ye sahip ilk grubu bul, world position'ını döner.
 */
function getFloorPlanGroupWorldOrigin(floorPlanId, scene) {
  if (!scene || !floorPlanId) return null;
  let found = null;
  scene.traverse(obj => {
    if (found) return;
    // Floor plan group'ları userData.floorPlanId taşıyor (layout dispatch sırasında set ediyor)
    if (obj.userData?.floorPlanId === floorPlanId &&
        (obj.userData?.name === 'mimariFloorPlan' || obj.isGroup || obj.type === 'Group')) {
      found = obj;
    }
  });
  if (!found) return null;
  const pos = new THREE.Vector3();
  found.getWorldPosition(pos);
  return { x: pos.x, y: pos.y };
}

/**
 * floorPlanId verilen kat planının world origin'ini döner.
 * Öncelik:
 *   1. Scene traversal — floor plan group'unun GERÇEK world pozisyonu
 *      (bbox normalize + layoutOffset uygulandıktan sonra).
 *   2. Fallback: documentBounds.minX/maxY (documentGroup pos — layout
 *      offset/bbox normalize'den önce, hafif sapma olabilir).
 */
export function getFloorPlanOrigin(floorPlanId, scene = null) {
  if (!floorPlanId) return null;
  const targetScene = scene || _sceneRef;
  if (targetScene) {
    const sceneOrigin = getFloorPlanGroupWorldOrigin(floorPlanId, targetScene);
    if (sceneOrigin) return sceneOrigin;
  }
  // Fallback: bounds-based
  const bounds = getGlobalDocumentBounds();
  if (!bounds?.length) return null;
  const fp = bounds.find(b => b.floorPlanId === floorPlanId);
  if (!fp) return null;
  return { x: fp.minX, y: fp.maxY };
}

/**
 * Mutlak (world) pozisyondan kat planına göre local pozisyon üret.
 * floorPlanId yoksa veya origin alınamazsa null döner — caller fallback yapsın.
 */
export function worldToLocal(worldPos, floorPlanId, scene = null) {
  if (!worldPos) return null;
  const origin = getFloorPlanOrigin(floorPlanId, scene);
  if (!origin) return null;
  return {
    x: (worldPos.x ?? 0) - origin.x,
    y: (worldPos.y ?? 0) - origin.y,
    z: worldPos.z ?? 0,
  };
}

/**
 * Local pozisyondan world pozisyon üret. floorPlanId yoksa null döner.
 */
export function localToWorld(localPos, floorPlanId, scene = null) {
  if (!localPos) return null;
  const origin = getFloorPlanOrigin(floorPlanId, scene);
  if (!origin) return null;
  return {
    x: (localPos.x ?? 0) + origin.x,
    y: (localPos.y ?? 0) + origin.y,
    z: localPos.z ?? 0,
  };
}

/**
 * Tesisat item'ı için world pozisyonunu döner.
 *
 * Geçici tasarım: localPosition mekanizması devre dışı bırakıldı çünkü
 * floor plan origin (scene traverse) save/reload arasında binlerce birim
 * kayabiliyor (origin tutarsızlığı → tesisat ekran dışına gidiyor).
 *
 * Layout determinizmi (sitePlan serialization + bbox.position.set(0,0,0) —
 * commit 6ba691a) absolute pozisyonların reload sonrası doğru hizalanmasını
 * sağlıyor; bu yüzden artık sadece absolute world pozisyonu kullanıyoruz.
 *
 * localPosition alanı slice/userData'da kalmaya devam ediyor (geriye dönük
 * uyum), ama rebuild yolunda kullanılmıyor.
 */
export function resolveWorldPosition(item) {
  return item?.position || null;
}
